import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { AuthErrorCodes, type User } from 'firebase/auth'
import { m } from 'framer-motion'
import { Loader2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PopupMethod } from '@/types/common'
import { parseAuthErrorMessage } from '@/utils/parseErrorMessages'
import { getSupportUrl } from '@/utils/url'
import { useToast } from '@/hooks/useToast'
import { useTrackedCallback } from '@/hooks/useTracking'
import { useAuthStore } from '@/store/authStore'
import { SignUpForm, type SignUpValues } from '@/components/form/SignUpForm'

export default function SignUpPage() {
  const router = useRouter()
  const isRedirectedRef = useRef<boolean>(false)
  const searchParams = useSearchParams()
  const invite = searchParams.get('invite')
  const { t } = useTranslation()
  const { toast } = useToast()
  const status = useAuthStore((state) => state.status)
  const signUp = useAuthStore((state) => state.signUp)
  const [submitting, setSubmitting] = useState(false)
  const loginWithPopup = useAuthStore((state) => state.loginWithPopup)
  const [user, setUser] = useState<User | null>(null)

  const values: SignUpValues | undefined = useMemo(() => {
    try {
      if (!invite) throw new Error('Empty invite')
      const { id, name, email } = JSON.parse(Buffer.from(invite, 'base64').toString('utf-8'))
      if ([id, name, email].some((v) => !v)) throw new Error('Invalid invite')
      return { name, email, password: '', confirmPassword: '', receiveUpdates: false }
    } catch {
      return undefined
    }
  }, [invite])

  useEffect(() => {
    if (isRedirectedRef.current) return
    if (user) {
      if (status === 'ready') {
        isRedirectedRef.current = true
        toast({ variant: 'success', description: t('auth:Toast.Success.SignIn') })
        router.push('/identity/')
      }
      if (status === 'unverified') {
        isRedirectedRef.current = true
        router.push('/auth/verify-email/')
      }
    }
  }, [router, status, t, toast, user])

  const handleSignUp = useTrackedCallback(
    async (data: SignUpValues) => {
      try {
        setSubmitting(true)
        const user = await signUp(data)
        setUser(user)
      } catch (e: any) {
        setSubmitting(false)
        if (e instanceof Error && e.message.includes('Too Many Requests'))
          toast({ variant: 'error', description: 'Too many attempts, please try again in 5 minutes' })
        else {
          const errorMessage = parseAuthErrorMessage(e, 'Unknown error: signUp')
          toast({
            variant: 'error',
            description: errorMessage?.message,
            learnMoreLink: getSupportUrl(errorMessage?.kbArticleId)
          })
        }
      }
    },
    [signUp, toast],
    'signup-via-email'
  )

  const handlePopupLogin = useTrackedCallback(
    async (method: PopupMethod) => {
      try {
        setSubmitting(true)
        const user = await loginWithPopup(method)
        setUser(user)
      } catch (e: any) {
        setSubmitting(false)
        if (e.code === AuthErrorCodes.MFA_REQUIRED) {
          router.push('/auth/two-factor')
        } else {
          const errorMessage = parseAuthErrorMessage(e, 'Unknown error: loginWithPopup')
          toast({
            variant: 'error',
            description: errorMessage?.message,
            learnMoreLink: getSupportUrl(errorMessage?.kbArticleId)
          })
        }
      }
    },
    [loginWithPopup, router, toast],
    'signup-via-popup'
  )

  return (
    <m.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={'relative grid h-auto w-full place-items-center '}
    >
      {submitting ? (
        <Loader2Icon className={'h-8 w-8 animate-spin text-primary'} />
      ) : (
        <div>
          <SignUpForm onSubmit={handleSignUp} values={values} onPopupLogin={handlePopupLogin} />
          <div className={'my-5 flex h-7 justify-center gap-4 text-sm/4'}>
            <a
              className={'font-bold underline'}
              href={'https://myassets.com/cookie-policy'}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              {t('auth:CookiePolicy')}
            </a>
          </div>
        </div>
      )}
    </m.main>
  )
}

SignUpPage.isAuthRoute = true
